import { useLocalStorage, useSessionStorage } from "@mantine/hooks";
import * as _ from "lodash-es";
import type {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_RowData,
  MRT_SortingState,
  MRT_Updater
} from "mantine-react-table-open";
import { useMemo } from "react";
import type { SiteStatusTabs } from "../SiteStatus";
import { dropInvalidColumnFilters } from "./utils/dropInvalidColumnFilters";
import { parseStoredPageSize } from "./utils/parseStoredPageSize";
import {
  parseStoredSessionState,
  type StoredSessionState
} from "./utils/parseStoredSessionState";

interface UseSiteStatusTableStateOptions<TData extends MRT_RowData> {
  storageKey: string;
  tab: SiteStatusTabs;
  columns: Array<MRT_ColumnDef<TData>>;
  defaultSorting: MRT_SortingState;
}

/**
 * Keeps the page size in localStorage and the page index, filters, search and
 * sorting in sessionStorage (per tab), keyed by view and active/inactive tab.
 */
export function useSiteStatusTableState<TData extends MRT_RowData>({
  storageKey,
  tab,
  columns,
  defaultSorting
}: UseSiteStatusTableStateOptions<TData>) {
  const [storedPageSize, setStoredPageSize] = useLocalStorage<unknown>({
    key: `${storageKey}:pageSize`,
    defaultValue: undefined,
    getInitialValueInEffect: false,
    sync: false
  });
  const [storedSessionState, setStoredSessionState] =
    useSessionStorage<unknown>({
      key: `${storageKey}:${tab}:state`,
      defaultValue: undefined,
      getInitialValueInEffect: false
    });

  const pageSize = parseStoredPageSize(storedPageSize);
  const defaults = useMemo<StoredSessionState>(
    () => ({
      pageIndex: 0,
      columnFilters: [],
      globalFilter: "",
      sorting: defaultSorting
    }),
    [defaultSorting]
  );
  const sessionState = useMemo(() => {
    const parsed = parseStoredSessionState(storedSessionState, defaults);
    return {
      ...parsed,
      columnFilters: dropInvalidColumnFilters(parsed.columnFilters, columns)
    };
  }, [storedSessionState, defaults, columns]);

  const pagination: MRT_PaginationState = {
    pageIndex: sessionState.pageIndex,
    pageSize
  };

  function updateSessionState(changes: Partial<StoredSessionState>) {
    setStoredSessionState((previous) => ({
      ...parseStoredSessionState(previous, defaults),
      ...changes
    }));
  }

  function setPagination(updater: MRT_Updater<MRT_PaginationState>) {
    const next = resolveUpdater(updater, pagination);
    if (next.pageSize !== pageSize) {
      setStoredPageSize(next.pageSize);
      updateSessionState({ pageIndex: 0 });
      return;
    }
    updateSessionState({ pageIndex: next.pageIndex });
  }

  function setColumnFilters(updater: MRT_Updater<MRT_ColumnFiltersState>) {
    const columnFilters = resolveUpdater(updater, sessionState.columnFilters);
    if (_.isEqual(columnFilters, sessionState.columnFilters)) {
      return;
    }
    updateSessionState({ columnFilters, pageIndex: 0 });
  }

  function setGlobalFilter(updater: MRT_Updater<string>) {
    const globalFilter =
      resolveUpdater(updater, sessionState.globalFilter) ?? "";
    if (globalFilter === sessionState.globalFilter) {
      return;
    }
    updateSessionState({ globalFilter, pageIndex: 0 });
  }

  function setSorting(updater: MRT_Updater<MRT_SortingState>) {
    const sorting = resolveUpdater(updater, sessionState.sorting);
    if (_.isEqual(sorting, sessionState.sorting)) {
      return;
    }
    updateSessionState({ sorting, pageIndex: 0 });
  }

  function setPageIndex(pageIndex: number) {
    updateSessionState({ pageIndex });
  }

  return {
    state: {
      pagination,
      columnFilters: sessionState.columnFilters,
      globalFilter: sessionState.globalFilter,
      sorting: sessionState.sorting
    },
    setPagination,
    setColumnFilters,
    setGlobalFilter,
    setSorting,
    setPageIndex
  };
}

function resolveUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function"
    ? (updater as (old: T) => T)(previous)
    : updater;
}
